import { useState, useEffect } from 'react';
import { 
  type User, 
  signInWithPopup, 
  signInWithRedirect,
  signOut as firebaseSignOut,
  onAuthStateChanged 
} from 'firebase/auth';
import { auth, googleProvider } from '../config/firebase';

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const signInWithGoogle = async () => {
    // Set loading to true when login process starts
    setLoading(true); 
    try {
      // Try popup first
      await signInWithPopup(auth, googleProvider);
      // onAuthStateChanged will handle the rest automatically
    } catch (error: any) {
      console.error('Error signing in with Google:', error);
      
      // If popup is blocked, fallback to redirect
      if (error.code === 'auth/popup-blocked') {
        try {
          await signInWithRedirect(auth, googleProvider);
          // Redirect will handle the authentication, no need to set loading to false here
        } catch (redirectError) {
          console.error('Error with redirect sign-in:', redirectError);
          setLoading(false);
          throw redirectError;
        }
      } else {
        // If other error, make sure loading returns to false
        setLoading(false);
        throw error;
      }
    }
  };

  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  return {
    user,
    loading,
    signInWithGoogle,
    signOut
  };
}